import { useEffect, useRef, useState } from "react";
import { safeLinkHref } from "../../editor/textRuns";
import {
  readTextSelectionLinkInfo,
  TEXT_LINK_REQUEST_EVENT,
} from "../../editor/textFormatBridge";

/** Закрыть всплывашку нажатием мимо неё и Escape. */
function useDismiss(open: boolean, rootRef: React.RefObject<HTMLElement | null>, close: () => void) {
  useEffect(() => {
    if (!open) return;
    const onDown = (ev: PointerEvent) => {
      if (!rootRef.current?.contains(ev.target as Node)) close();
    };
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") {
        ev.stopPropagation();
        close();
      }
    };
    document.addEventListener("pointerdown", onDown, true);
    window.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      window.removeEventListener("keydown", onKey, true);
    };
  }, [open, rootRef, close]);
}

/** Фон надписи: светлые плашки, пара ярких и тёмная — как в Miro. */
export const TEXT_FILL_COLORS = [
  "#fef9c3",
  "#dcfce7",
  "#dbeafe",
  "#fce7f3",
  "#ffedd5",
  "#ede9fe",
  "#f1f5f9",
  "#ffffff",
  "#fde047",
  "#86efac",
  "#93c5fd",
  "#f9a8d4",
  "#fdba74",
  "#c4b5fd",
  "#cbd5e1",
  "#1a1a2e",
] as const;

/**
 * Фон всей надписи — плашка под текстом. Не путать с маркером: маркер
 * подсвечивает буквы куска, а фон заливает рамку надписи целиком.
 */
export function TextFillPicker({
  fill,
  disabled,
  onPick,
}: {
  fill: string | undefined;
  disabled?: boolean;
  /** null — убрать фон. */
  onPick: (color: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  useDismiss(open, rootRef, () => setOpen(false));
  const current = fill?.toLowerCase();

  return (
    <div className="wb-text-fill" ref={rootRef}>
      <button
        type="button"
        className={`wb-selection-toolbar__action-btn wb-text-fill__btn${fill ? " is-active" : ""}`}
        title="Фон надписи"
        aria-label="Фон надписи"
        aria-haspopup="dialog"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
      >
        <span
          className={`wb-text-fill__chip${fill ? "" : " is-none"}`}
          style={fill ? { background: fill } : undefined}
          aria-hidden="true"
        />
        Фон
      </button>
      {open ? (
        <div className="wb-text-fill__pop" role="dialog" aria-label="Фон надписи">
          <div className="wb-text-fill__grid">
            <button
              type="button"
              className={`wb-text-fill__swatch is-none${!fill ? " is-active" : ""}`}
              title="Без фона"
              aria-label="Без фона"
              onClick={() => {
                setOpen(false);
                onPick(null);
              }}
            />
            {TEXT_FILL_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                className={`wb-text-fill__swatch${current === c ? " is-active" : ""}`}
                style={{ background: c }}
                title={c}
                aria-label={`Фон ${c}`}
                onClick={() => {
                  setOpen(false);
                  onPick(c);
                }}
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Ссылка. Во время набора — на выделенный кусок (или на ссылку под
 * курсором), иначе — на всю надпись. Ctrl+K в поле набора открывает то же
 * окошко.
 */
export function TextLinkButton({
  disabled,
  wholeHref,
  onApplyWhole,
  onApplySelection,
}: {
  disabled?: boolean;
  /** Ссылка, если вся надпись — одна ссылка. */
  wholeHref: string | null;
  /** Поставить или снять ссылку на всей надписи. */
  onApplyWhole: (href: string | null) => void;
  /** Поставить или снять ссылку на выделенном куске; false — поле не открыто. */
  onApplySelection: (href: string | null) => boolean;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [existing, setExisting] = useState<string | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  /** К чему применять: к куску в поле набора или ко всей надписи. */
  const targetRef = useRef<"selection" | "whole">("whole");
  useDismiss(open || Boolean(hint), rootRef, () => {
    setOpen(false);
    setHint(null);
  });

  const openPopover = () => {
    setError(null);
    const info = readTextSelectionLinkInfo();
    if (info) {
      if (!info.canLink) {
        // Поле открыто, но ни выделения, ни ссылки под курсором.
        setOpen(false);
        setHint("Выделите слова, на которые поставить ссылку");
        return;
      }
      targetRef.current = "selection";
      setExisting(info.href);
      setDraft(info.href ?? "");
    } else {
      targetRef.current = "whole";
      setExisting(wholeHref);
      setDraft(wholeHref ?? "");
    }
    setHint(null);
    setOpen(true);
  };

  useEffect(() => {
    const onRequest = () => openPopover();
    window.addEventListener(TEXT_LINK_REQUEST_EVENT, onRequest);
    return () => window.removeEventListener(TEXT_LINK_REQUEST_EVENT, onRequest);
  });

  useEffect(() => {
    if (open) inputRef.current?.focus({ preventScroll: true });
  }, [open]);

  const apply = (href: string | null) => {
    if (href !== null) {
      const safe = safeLinkHref(href);
      if (!safe) {
        setError("Не похоже на адрес сайта");
        return;
      }
      href = safe;
    }
    setOpen(false);
    if (targetRef.current === "selection" && onApplySelection(href)) return;
    onApplyWhole(href);
  };

  return (
    <div className="wb-text-link" ref={rootRef}>
      <button
        type="button"
        className={`wb-selection-toolbar__action-btn${open ? " is-active" : ""}`}
        title="Ссылка (Ctrl+K)"
        aria-label="Ссылка"
        aria-haspopup="dialog"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : openPopover())}
      >
        🔗
      </button>
      {hint ? (
        <div className="wb-text-link__pop wb-text-link__pop--hint" role="status">
          {hint}
        </div>
      ) : null}
      {open ? (
        <form
          className="wb-text-link__pop"
          role="dialog"
          aria-label="Ссылка"
          onSubmit={(ev) => {
            ev.preventDefault();
            apply(draft.trim() ? draft : null);
          }}
        >
          <input
            ref={inputRef}
            className="wb-text-link__input"
            type="text"
            inputMode="url"
            placeholder="https://…"
            aria-label="Адрес ссылки"
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              setError(null);
            }}
            onKeyDown={(e) => {
              // Панель доски не должна перехватывать набор адреса.
              e.stopPropagation();
              if (e.key === "Escape") {
                e.preventDefault();
                setOpen(false);
              }
            }}
          />
          <div className="wb-text-link__actions">
            <button type="submit" className="wb-btn wb-btn--primary wb-btn--sm">
              Готово
            </button>
            {existing ? (
              <button
                type="button"
                className="wb-btn wb-btn--ghost wb-btn--sm"
                onClick={() => apply(null)}
              >
                Убрать
              </button>
            ) : null}
            {existing ? (
              <a
                className="wb-text-link__open"
                href={existing}
                target="_blank"
                rel="noopener noreferrer"
              >
                Открыть ↗
              </a>
            ) : null}
          </div>
          {error ? <div className="wb-text-link__error">{error}</div> : null}
        </form>
      ) : null}
    </div>
  );
}
